import React, { ReactElement } from 'react';
import PropTypes from 'prop-types';

import ButtonRow from '../ButtonRow/ButtonRow';

import IconContext from '../context/IconContext';

// Define the type of the React component being given via context
type SvgComponentProps = React.SVGProps<SVGSVGElement> 
// Create an interface containing the icons needed for this component
interface IIconContext {
    IconCancelWhite: React.ComponentType<SvgComponentProps>;
    IconError: React.ComponentType<SvgComponentProps>;
    IconInfo: React.ComponentType<SvgComponentProps>;
    IconSuccess: React.ComponentType<SvgComponentProps>;
    IconWarning: React.ComponentType<SvgComponentProps>;

    // To use custom SVGs, update the config to define these keys.
    IconSnackbarClose: React.ComponentType<SvgComponentProps>;
    IconSnackbarError: React.ComponentType<SvgComponentProps>;
    IconSnackbarInfo: React.ComponentType<SvgComponentProps>;
    IconSnackbarSuccess: React.ComponentType<SvgComponentProps>;
    IconSnackbarWarning: React.ComponentType<SvgComponentProps>;
}
export interface SnackbarProps extends React.HTMLAttributes<HTMLElement> {
    isHidden: boolean;
    primaryMessage?: String;
    secondaryMessage?: String;
    type?: 'success' | 'informational' | 'error' | 'warning'
    noTimeout?: boolean;
    // Baseline arguments
    children?: React.ReactNode;
    className?: string;
    // Functions
    onClose: () => void;
}

/**
 * A larger feedback pop-up with actions. Disappears after an 8 second timeout.
 * 
 * ## _
 * ### Arguments
 * @param {boolean} isHidden - Controls the visibility of the snackbar. Defaults to true.
 * @param {string} primaryMessage - A string used as the main message of the snackbar. Usually brief.
 * @param {string} secondaryMessage - A string used as the secondary message of the snackbar. Usually more descriptive.
 * @param {string} type - One of 'success', 'informational', 'error', or 'warning', meant for styling the colors of the snackbar as well as the icon. Defaults to 'informational'.
 * @param {boolean} noTimeout - A flag that disables the 8 second timer for the snackbar disappearing.
 * @param {React.ReactNode} children - Used as the list of actions the snackbar can have. Typically should be many `Button` components.
 * @param {string} className - CSS classes to apply to the snackbar.
 * 
 * ## _
 * ### Functions
 * @param {function} onClose - Function that is called when the snackbar is closed.
 * 
 * ## _
 * ### Icons
 * Declare these in your application's icon configuration.
 * @inner `IconSnackbarClose` - An optional icon override for the close icon.
 * @inner `IconSnackbarError` - An optional icon override for the icon for error-type snackbars.
 * @inner `IconSnackbarInfo` - An optional icon override for the icon for informational-type snackbars.
 * @inner `IconSnackbarSuccess` - An optional icon override for the icon for success-type snackbars.
 * @inner `IconSnackbarWarning` - An optional icon override for the icon for warning-type snackbars.
 */
class Snackbar extends React.Component<SnackbarProps> {
    timeoutId: NodeJS.Timeout | null = null;
    isHover = false;
    isFocus = false;
    // Used to store the remaining time for the timer.
    timer = -1;
    // Stores Unix timestamp of when the timer was last started.
    timeoutStart = -1;

    // Establish the context and tell TypeScript that we're TOTALLY having it in the shape of this thing.
    static contextType = IconContext;
    context!: IIconContext;

    static propTypes = {
        isHidden: PropTypes.bool.isRequired,
        primaryMessage: PropTypes.string,
        secondaryMessage: PropTypes.string,
        type: PropTypes.oneOf(['success', 'informational', 'error', 'warning']),
        noTimeout: PropTypes.bool,
        children: PropTypes.node,
        className: PropTypes.string,
        onClose: PropTypes.func,
    }

    static defaultProps = {
        primaryMessage: "Success!",
        type: 'informational',
        noTimeout: false,
        className: "",
    }

    componentDidUpdate(prevProps: { isHidden: any; }) {
        // Preliminary reset timer, to prevent extra function calls.
        this.timer = -1;
        if(prevProps.isHidden && !this.props.isHidden && !this.timeoutId && !this.props.noTimeout){
            // This means we're showing, start a timeout and record the start time.
            // In the event the timeout flag is false, this timer is never started and will remain null.
            this.timeoutId = setTimeout(this.props.onClose, 8000);
            this.timer = 8000;
            this.timeoutStart = Date.now();
        }
        else if(this.props.isHidden && this.timeoutId){
            // This means we're hiding, and an active timeout is happening. Clear it.
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }

    componentDidMount() {
        if(!this.props.isHidden){
            this.timeoutId = setTimeout(this.props.onClose, 8000);
            this.timer = 8000;
            this.timeoutStart = Date.now();
        }
    }

    componentWillUnmount() {
        if(this.timeoutId){
            clearTimeout(this.timeoutId);
        }
    }

    // There's probably a nicer way of doing this, this seems extra
    handleMouseOver = () => {
        if(!this.isHover){
            this.isHover = true;
            this.handlePause();
        }
    }

    handleMouseOut = () => {
        if(this.isHover){
            this.isHover = false;
            this.handlePause();
        }
    }
    
    handleFocusIn = () => {
        if(!this.isFocus){
            this.isFocus = true;
            this.handlePause();
        }
    }

    handleFocusOut = () => {
        if(this.isFocus){
            this.isFocus = false;
            this.handlePause();
        }
    }

    handlePause = () => {
        if(this.isHover || this.isFocus){
            // If pause, subtract the time elapsed and clear timeout
            if(this.timeoutId){
                clearTimeout(this.timeoutId);
                this.timeoutId = null;
                this.timer -= (Date.now() - this.timeoutStart);
            }
        }
        else{
            // If unpause, restart the timer with remaining time and store time resumed at
            if(this.timer !== -1){
                this.timeoutId = setTimeout(this.props.onClose, this.timer);
                this.timeoutStart = Date.now();
            }
        }
    }

    render() {
        const { isHidden, primaryMessage, secondaryMessage, type, children, className, onClose, ...rest } = this.props;
        const { 
            IconCancelWhite, IconError, IconInfo, IconSuccess, IconWarning,
            IconSnackbarClose, IconSnackbarError, IconSnackbarInfo, IconSnackbarSuccess, IconSnackbarWarning 
        } = this.context;

        const CloseIcon = IconSnackbarClose || IconCancelWhite;
        const ErrorIcon = IconSnackbarError || IconError;
        const InfoIcon = IconSnackbarInfo || IconInfo;
        const SuccessIcon = IconSnackbarSuccess || IconSuccess;
        const WarningIcon = IconSnackbarWarning || IconWarning;

        let classes = `o-snackbar`;
        classes += ` o-snackbar--${type}`;

        if (!isHidden) classes += ` o-snackbar--shown`;
        if (className) classes += ` ${className}`

        return (
            <div className={classes} onMouseOver={this.handleMouseOver} onMouseLeave={this.handleMouseOut}>
                <div className="o-snackbar__content">
                    
                    {type === "success" && <SuccessIcon className="o-snackbar__message-icon" />}
                    {type === "error" && <ErrorIcon className="o-snackbar__message-icon" />}
                    {type === "warning" && <WarningIcon className="o-snackbar__message-icon" />}
                    {type === "informational" && <InfoIcon className="o-snackbar__message-icon" />}

                    <p className="o-snackbar__messages">
                        <span className="o-snackbar__primary-message">{primaryMessage}</span>
                        {secondaryMessage && <span className="o-snackbar__supplemental-message">{secondaryMessage}</span>}
                    </p>
                </div>
                <div className="o-snackbar__actions">
                    <ButtonRow className="o-snackbar__btn-wrapper">
                        {React.Children.map(children, (child) => {
                            if (React.isValidElement(child)) {
                                return React.cloneElement(child as ReactElement, {
                                    onFocus: this.handleFocusIn,
                                    onBlur: this.handleFocusOut
                                });
                            }
                            return child;
                        })}
                    </ButtonRow>
                    <CloseIcon className="o-snackbar__close-btn" onClick={onClose} onFocus={this.handleFocusIn} onBlur={this.handleFocusOut}/>
                </div>
            </div >
        )
    }
}

export default Snackbar;